'use client';

import { devLog } from '@/lib/logging';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, type Dispatch } from 'react';
import {
  POST_QUESTIONNAIRE_SLUGS,
  QUESTIONNAIRE_STEP_IDS,
} from '../config/steps';
import {
  createSession,
  fetchSession,
  useUpdateSession,
} from '../contexts/intakeSessionApi';
import {
  getStoredSessionId,
  isValidSessionId,
  SESSION_ID_KEY,
  storeSessionId,
} from '../contexts/intakeSessionIdStorage';
import { initialWizardState } from '../contexts/WizardContext';
import { restoreFromSession } from '../contexts/wizardReducer';
import type { UpdateSessionRequest, WizardAction, WizardState } from '../types';
import {
  MEMBER_DETAILS_PATH,
  OPPORTUNITY_ID_PARAM,
} from '../utils/opportunityPrefill.constants';

const SAVE_DEBOUNCE_TIME_MS = 1250;

const isQuestionnaireComplete = (completedSteps: string[]): boolean =>
  QUESTIONNAIRE_STEP_IDS.every(id => completedSteps.includes(id));

const isOnQuestionnaireStep = (): boolean =>
  !POST_QUESTIONNAIRE_SLUGS.some(slug =>
    window.location.pathname.endsWith(`/${slug}`)
  );

export const stateToUpdateRequest = (
  state: WizardState
): UpdateSessionRequest => ({
  formData: state.formData,
  completedSteps: state.completedSteps,
  leadId: state.leadId || undefined,
  contactId: state.contactId || undefined,
  accountId: state.accountId || undefined,
  opportunityId: state.opportunityId || undefined,
});

/**
 * Initializes the intake session from storage/API, persists state changes to the API,
 * and logs wizard state updates in development.
 */
export const useWizardSessionLifecycle = (
  state: WizardState,
  dispatch: Dispatch<WizardAction>
) => {
  const { update: updateSession } = useUpdateSession();
  const router = useRouter();

  const isInitialized = useRef(false);
  const isSavingToApi = useRef(false);
  const lastSavedState = useRef<string>('');

  /**
   * Save state to API with error logging
   */
  const saveToApi = useCallback(
    async (currentState: WizardState) => {
      if (!currentState.sessionId || isSavingToApi.current) {
        return;
      }

      const stateJson = JSON.stringify(stateToUpdateRequest(currentState));

      // Skip if state hasn't changed
      if (stateJson === lastSavedState.current) {
        return;
      }

      isSavingToApi.current = true;
      dispatch({ type: 'SET_SAVING', payload: true });

      try {
        await updateSession(
          currentState.sessionId,
          stateToUpdateRequest(currentState)
        );

        lastSavedState.current = stateJson;
      } catch (error) {
        console.error('Error saving intake session to API:', error);

        dispatch({
          type: 'SET_ERROR',
          payload: 'Error communicating with the server.',
        });
      } finally {
        isSavingToApi.current = false;
        dispatch({ type: 'SET_SAVING', payload: false });
      }
    },
    [dispatch, updateSession]
  );

  // Initialize: restore from API or create new session
  useEffect(() => {
    if (isInitialized.current) {
      return;
    }
    isInitialized.current = true;

    const initialize = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        // Allow forcing a new session via ?new_session query param
        const params = new URLSearchParams(window.location.search);
        if (params.has('new_session')) {
          localStorage.removeItem(SESSION_ID_KEY);
          params.delete('new_session');
          const newUrl =
            window.location.pathname +
            (params.size ? `?${params}` : '') +
            window.location.hash;
          window.history.replaceState({}, '', newUrl);
        }

        if (params.has(OPPORTUNITY_ID_PARAM)) {
          localStorage.removeItem(SESSION_ID_KEY);
        }

        const storedSessionId = getStoredSessionId();

        if (storedSessionId) {
          // Try to restore from API
          const apiSession = await fetchSession(storedSessionId);

          if (apiSession) {
            dispatch({ type: 'RESTORE_SESSION', payload: apiSession });
            lastSavedState.current = JSON.stringify(
              stateToUpdateRequest(restoreFromSession(apiSession))
            );
            dispatch({ type: 'SET_LOADING', payload: false });

            if (
              isQuestionnaireComplete(apiSession.completedSteps ?? []) &&
              isOnQuestionnaireStep()
            ) {
              router.replace(MEMBER_DETAILS_PATH);
            }
            return;
          }

          // Session not found in API (expired or invalid), create new one
          console.error(
            'Session not found in API, creating new session:',
            storedSessionId
          );
        }

        // Create a new session
        const newSession = await createSession('intake_v2');

        if (!isValidSessionId(newSession.sessionId)) {
          throw new Error('Session creation returned invalid session ID');
        }
        dispatch({ type: 'SET_SESSION_ID', payload: newSession.sessionId });
        storeSessionId(newSession.sessionId);
        lastSavedState.current = JSON.stringify(
          stateToUpdateRequest(initialWizardState)
        );
      } catch (error) {
        console.error('Error initializing intake session:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to initialize session. Please try again.',
        });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initialize();
  }, [dispatch, router]);

  // Persist to API on state changes
  useEffect(() => {
    if (!isInitialized.current) {
      return;
    }

    if (state.error) return;

    const timeout = setTimeout(() => saveToApi(state), SAVE_DEBOUNCE_TIME_MS);

    return () => clearTimeout(timeout);
  }, [state, saveToApi]);

  // Log state changes in development
  useEffect(() => {
    if (isInitialized.current) {
      devLog('Wizard state updated:', state);
    }
  }, [state]);
};
